// Step for entering one-time passcode

import React, { useEffect, useState } from 'react';
import { RefreshCwIcon } from 'lucide-react';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { AuthStepCard } from './AuthStepCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Field, FieldError, FieldGroup } from '@/components/ui/field';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { useAuth, InputValidationError, useValidatedInput } from '../../hooks';
import { analyticsService } from '../../services/analyticsService';
import { requestOTP, verifyOTP } from '../../transport/api/publicApi';
import { markAuthCompletionPending } from '../../utilities/authCompletion';

const RESEND_COOLDOWN_SECONDS = 30;

const extractApiErrorMessage = (err: unknown, fallback: string): string => {
  if (typeof err === 'object' && err !== null) {
    const maybe = (err as { response?: { error?: { message?: unknown } } })
      .response?.error?.message;
    if (typeof maybe === 'string' && maybe) return maybe;
  }
  return fallback;
};

interface OtpStepProps {
  email: string;
  onChangeEmail: () => void;
}

const OtpStep: React.FC<OtpStepProps> = ({ email, onChangeEmail }) => {
  const { refetchUser } = useAuth();
  const [resendStatus, setResendStatus] = useState<'idle' | 'cooldown'>('idle');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    if (resendStatus === 'cooldown' && cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            setResendStatus('idle');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendStatus, cooldownSeconds]);

  const otp = useValidatedInput({
    rules: ['onlyNumbers', 'minLength:6', 'maxLength:6'],
    onSubmit: async (otpValue) => {
      if (!email) {
        throw new InputValidationError(
          'No email provided. Please go back and enter your email.'
        );
      }

      try {
        await verifyOTP(email, otpValue);
        analyticsService.trackFormSubmit('otp_verification', 'otp');
        markAuthCompletionPending();
        // refetchUser flips the user from null to set, which useAuthPostLogin
        // listens for. Everything after (toast, welcome step, checkout resume)
        // is owned there, not here.
        await refetchUser();
      } catch (err) {
        throw new InputValidationError(
          extractApiErrorMessage(
            err,
            'Failed to validate code. Please try again.'
          )
        );
      }
    },
  });

  const handleResendCode = async () => {
    if (!email) {
      setResendError('No email provided.');
      return;
    }

    if (resendStatus === 'cooldown') {
      return;
    }

    analyticsService.trackButtonClick('Resend Code', {
      link_location: 'email_verification',
    });

    setResendError(null);
    setResendStatus('cooldown');
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS);

    try {
      await requestOTP(email);
    } catch (err) {
      setResendStatus('idle');
      setCooldownSeconds(0);
      setResendError(
        extractApiErrorMessage(err, 'Failed to resend code. Please try again.')
      );
    }
  };

  return (
    <AuthStepCard>
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">Enter Passcode</CardTitle>
        <CardDescription className="whitespace-pre-line">
          {`Check your email for the code:\n${email}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-3">
        <form
          className="flex w-fit flex-col items-center gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void otp.submit();
          }}
        >
          <div className="flex w-full items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              Didn&apos;t receive it?
            </p>
            {resendStatus === 'cooldown' ? (
              <p className="text-sm text-muted-foreground">
                Retry in {cooldownSeconds}s
              </p>
            ) : (
              <Button
                onClick={handleResendCode}
                variant="outline"
                size="xs"
                className="cursor-pointer"
                type="button"
                disabled={otp.isSubmitting}
              >
                <RefreshCwIcon data-icon="inline-start" />
                Resend code
              </Button>
            )}
          </div>
          <FieldGroup>
            <Field data-invalid={!!otp.error}>
              <InputOTP
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                value={otp.value}
                disabled={otp.isSubmitting}
                aria-invalid={!!otp.error}
                onChange={(value) => {
                  otp.setValue(value);
                  if (otp.error) otp.setError(null);
                  if (value.length === 6) {
                    void otp.submit(value);
                  }
                }}
              >
                <InputOTPGroup className="*:data-[slot=input-otp-slot]:h-12 *:data-[slot=input-otp-slot]:w-11 *:data-[slot=input-otp-slot]:text-xl">
                  <InputOTPSlot index={0} aria-invalid={!!otp.error} />
                  <InputOTPSlot index={1} aria-invalid={!!otp.error} />
                  <InputOTPSlot index={2} aria-invalid={!!otp.error} />
                  <InputOTPSlot index={3} aria-invalid={!!otp.error} />
                  <InputOTPSlot index={4} aria-invalid={!!otp.error} />
                  <InputOTPSlot index={5} aria-invalid={!!otp.error} />
                </InputOTPGroup>
              </InputOTP>
              {otp.error ? <FieldError>{otp.error}</FieldError> : null}
            </Field>
            <Button
              type="submit"
              disabled={otp.isSubmitting}
              className="w-full cursor-pointer"
              size="xl"
            >
              {otp.isSubmitting ? 'Verifying...' : 'Submit'}
            </Button>
            <Button
              onClick={onChangeEmail}
              variant="secondary"
              className="w-full cursor-pointer"
              type="button"
              disabled={otp.isSubmitting}
              size="xl"
            >
              Try different email
            </Button>
          </FieldGroup>
        </form>
        {resendError && (
          <Alert variant="destructive" className="w-full">
            <AlertDescription>{resendError}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </AuthStepCard>
  );
};

export default OtpStep;
